import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ReactSVG } from 'react-svg';
import classNames from 'classnames';
import { RequisiteAutoGenModal, RequisiteModal } from 'Components/RequisiteComponents';
import { UIAddButton } from 'Components/UIComponents/UIAddButton';
import UIButton from 'Components/UIComponents/UIButton';
import UIModal from 'Components/UIComponents/UIModal';
import { Signer } from 'Interfaces/Document';
import {
  defaultRequisiteTypesOrder,
  Requisite,
  RequisiteSiblingValues,
  RequisiteType,
  RequisiteValueType,
} from 'Interfaces/Requisite';
import { User } from 'Interfaces/User';
import { getInitials } from 'Utils/formatters';
import {
  selectRequisite,
  selectRequisitesByType,
  selectRequisitesByTypeAndActiveTab,
  selectUser,
} from 'Utils/selectors';

import RequisiteTabItem from '../RequisiteTabItem';

import RequisiteListItem from './RequisiteListItem';

import CloseIcon from 'Assets/images/icons/close-icon.svg';

const getRequisiteStringsByType = (type: RequisiteType) => {
  switch (type) {
    case RequisiteType.SIGN:
      return {
        typeName: 'Signature',
        addTitle: 'Create New Signature',
        createdTitle: 'We created a signature for you',
        chooseTitle: 'Choose Signature',
        emptyModal: {
          header: 'No signatures are available yet.',
          text: [
            'Create your first signature to be able to sign any document.',
            'Your created signatures will appear here.',
          ],
        },
      };
    default:
      return {
        typeName: 'Initials',
        addTitle: 'Create New Initials',
        createdTitle: 'We created an initials for you',
        chooseTitle: 'Choose Initials',
        emptyModal: {
          header: 'No initials are available yet.',
          text: [
            'Create your first initial to be able to sign any document.',
            'Your created initials will appear here.',
          ],
        },
      };
  }
};

interface RequisiteSelectModalProps {
  signer?: Signer;
  type: RequisiteType;
  availableSignatureTypes?: RequisiteValueType[];
  onSelect: (requisite: Requisite) => void;
  onClose: () => void;
}

const RequisiteSelectModal = ({
  type,
  onSelect,
  onClose,
  signer,
  availableSignatureTypes = defaultRequisiteTypesOrder,
}: RequisiteSelectModalProps) => {
  const { name = '' }: User = useSelector(selectUser);
  const [requisiteValues, setRequisiteValues] = useState<RequisiteSiblingValues>({
    signatureValue: '',
    initialsValue: '',
  });
  const [selectedRequisite, setSelectedRequisite] = useState<Requisite>();
  const [isInItemView, setIsInItemView] = useState(false);
  const [isCustomCreate, setIsCustomCreate] = useState(false);
  const [activeTab, setActiveTab] = useState<RequisiteValueType>(
    availableSignatureTypes[0],
  );

  const strings = useMemo(() => getRequisiteStringsByType(type), [type]);

  const requisites = useSelector(state => selectRequisitesByType(state, { type }));

  const activeRequisites = useSelector(state =>
    selectRequisitesByTypeAndActiveTab(state, { type, activeTab }),
  );

  const siblingRequisite = useSelector((state: any) =>
    selectRequisite(state, { requisiteId: selectedRequisite?.siblingId || '' }),
  );

  const defaultValues = useMemo(() => {
    return isCustomCreate
      ? { defaultValues: requisiteValues, defaultFontFamilyType: 'dancingScript' }
      : {};
  }, [isCustomCreate, requisiteValues]);

  const closeItemView = useCallback(() => {
    setIsInItemView(false);
  }, []);

  const onCreateClick = useCallback(() => {
    setSelectedRequisite(undefined);
    setIsInItemView(true);
  }, []);

  const onCreateCustomClick = useCallback(() => {
    setSelectedRequisite(undefined);
    setIsCustomCreate(true);
    setIsInItemView(true);
  }, []);

  const onChangeValue = useCallback(
    value => {
      setRequisiteValues(prevState => {
        const newRequisiteValues:
          | Pick<RequisiteSiblingValues, 'initialsValue'>
          | Pick<RequisiteSiblingValues, 'signatureValue' | 'initialsValue'> =
          type === RequisiteType.SIGN
            ? { signatureValue: value, initialsValue: getInitials(value) }
            : { initialsValue: value };
        return { ...prevState, ...newRequisiteValues };
      });
    },
    [type],
  );

  const handleSelect = useCallback(() => {
    selectedRequisite && onSelect(selectedRequisite);
  }, [selectedRequisite, onSelect]);

  useEffect(() => {
    const value = signer?.userId ? signer?.name : name;
    setRequisiteValues({
      signatureValue: value || '',
      initialsValue: getInitials(value),
    });
  }, [signer, name, type]);

  if (isInItemView) {
    return (
      <UIModal
        className="requisiteModal__wrapper responsive"
        onClose={closeItemView}
        hideCloseIcon
      >
        <RequisiteModal
          onClose={closeItemView}
          requisiteItem={selectedRequisite}
          siblingRequisiteItem={selectedRequisite ? siblingRequisite : undefined}
          requisiteType={type}
          onSuccessfulSubmit={onSelect}
          title={selectedRequisite?.id ? 'Edit Signature' : 'Create New Signature'}
          buttonText={selectedRequisite?.id ? 'Update Signature' : 'Create Signature'}
          availableSignatureTypes={availableSignatureTypes}
          {...defaultValues}
        />
      </UIModal>
    );
  }

  if (
    requisites.length === 0 &&
    availableSignatureTypes.includes(RequisiteValueType.TEXT)
  )
    return (
      <UIModal className="requisiteModal__wrapper" onClose={onClose} hideCloseIcon>
        <RequisiteAutoGenModal
          requisiteValue={
            type === RequisiteType.SIGN
              ? requisiteValues.signatureValue
              : requisiteValues.initialsValue
          }
          siblingRequisiteValue={
            type === RequisiteType.SIGN
              ? requisiteValues.initialsValue
              : requisiteValues.signatureValue
          }
          title={signer?.name || name ? strings.createdTitle : 'Type your Signature'}
          onClose={onClose}
          requisiteType={type}
          onClickCustomCreate={onCreateCustomClick}
          onSuccessfulSubmit={onSelect}
          onChangeValue={onChangeValue}
        />
      </UIModal>
    );

  return (
    <UIModal
      className="requisiteModal__wrapper requisiteModal__wrapper-select responsive"
      onClose={onClose}
      hideCloseIcon
    >
      <div className="requisiteModal requisiteModal-select">
        <div className="requisiteModal__inner requisiteModal__inner-select">
          <div className="requisiteModal__header">
            <p className="requisiteModal__title">{strings.chooseTitle}</p>
            <ReactSVG
              src={CloseIcon}
              onClick={onClose}
              className="requisiteModal__header-icon"
            />
          </div>
          <div
            className={classNames('requisiteModal__nav-list-select-wrapper', {
              shadowed: requisites.length > 3,
            })}
          >
            <ul className="requisiteModal__nav-list requisiteModal__nav-list-select">
              {availableSignatureTypes.includes(RequisiteValueType.TEXT) && (
                <RequisiteTabItem
                  title="Typed"
                  onClick={() => setActiveTab(RequisiteValueType.TEXT)}
                  isActive={activeTab === RequisiteValueType.TEXT}
                />
              )}
              {availableSignatureTypes.includes(RequisiteValueType.DRAW) && (
                <RequisiteTabItem
                  title="Drawn"
                  onClick={() => setActiveTab(RequisiteValueType.DRAW)}
                  isActive={activeTab === RequisiteValueType.DRAW}
                />
              )}
              {availableSignatureTypes.includes(RequisiteValueType.UPLOAD) && (
                <RequisiteTabItem
                  title="Uploaded"
                  onClick={() => setActiveTab(RequisiteValueType.UPLOAD)}
                  isActive={activeTab === RequisiteValueType.UPLOAD}
                />
              )}
            </ul>
          </div>

          <div
            className={classNames(
              'requisiteModal__content',
              'requisiteModal__content-select',
            )}
          >
            <ul
              className={classNames(
                'requisiteModal__content',
                'requisiteModal__content-select',
                'requisiteModal__content-select--inner',
                {
                  'requisiteModal__content-select--empty': !activeRequisites.length,
                },
              )}
            >
              {activeRequisites.length ? (
                activeRequisites.map(requisite => (
                  <RequisiteListItem
                    key={requisite.id}
                    onItemClick={setSelectedRequisite}
                    onItemDoubleClick={handleSelect}
                    isSelected={selectedRequisite?.id === requisite.id}
                    onItemEdit={() => {
                      setSelectedRequisite(requisite);
                      setIsInItemView(true);
                    }}
                    requisite={requisite}
                  />
                ))
              ) : (
                <div className="requisiteModal__empty">
                  <div className="requisiteModal__empty-content">
                    <h4 className="requisiteModal__empty-content-header">
                      {strings.emptyModal.header}
                    </h4>
                    <p className="requisiteModal__empty-content-text">
                      {strings.emptyModal.text.map((text, index) =>
                        index === 0 ? (
                          text
                        ) : (
                          <Fragment key={text}>
                            <br />
                            {text}
                          </Fragment>
                        ),
                      )}
                    </p>
                  </div>
                  <UIButton
                    priority="primary"
                    className="requisiteModal__empty-button"
                    handleClick={onCreateClick}
                    title={strings.addTitle}
                  />
                </div>
              )}
            </ul>
            {!!activeRequisites.length && (
              <div className="requisiteModal__footer requisiteModal__footer-select">
                <UIAddButton
                  onClick={onCreateClick}
                  label={strings.addTitle}
                  wrapperClassName="requisiteModal__addButton-wrapper"
                  iconClassName="requisiteModal__addButton-icon"
                  labelClassName="requisiteModal__addButton-label"
                />
                <UIButton
                  handleClick={onCreateClick}
                  priority="primary"
                  className="requisiteModal__addButton--mobile"
                  title={strings.addTitle}
                />
                <UIButton
                  priority="primary"
                  title="Sign Now"
                  disabled={!selectedRequisite}
                  handleClick={handleSelect}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </UIModal>
  );
};

export default RequisiteSelectModal;
